import { useEffect } from 'react';
import GroupChannelListUI from '@sendbird/uikit-react/GroupChannelList/components/GroupChannelListUI';
import { useGroupChannelList } from '@sendbird/uikit-react/GroupChannelList/context';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useQuery } from 'react-query';
import { useChatPopupStore, useChatStore, useGroupChannelListStore } from '@store/chat';
import { GroupChannelCustomType } from '@asset/type/chat';
import { getStudentsExtrasApi } from '@apis/api/student/common';
import { getStudentStudyStatus } from '@asset/function/commonFunctions';
import UserAvatarProfile from '@components/common/UserAvatarProfile';
import { Button } from '@components/amc/Button';
import SearchInput from '@components/common/input/SearchInput';
import { ChatMessageType } from '@asset/enum/chatEnum';
import * as S from '@styles/chat/chat.style';
import CustomGroupChannelListSkeleton from './CustomGroupChannelListSkeleton';
import EmptyContent from '@components/common/EmptyContent';
import useSendbirdStateContext from '@sendbird/uikit-react/useSendbirdStateContext';
import { StudyStatusLight } from '@components/study-status/StudyStatusIndicator';
import { StudyStatus } from '@asset/enum/studyStatus';
import { IconSearch, IconX } from '@tabler/icons-react';
import DateTimeRelativeText from '@components/common/DateTimeRelativeText';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const CustomGroupChannelListHeader = ({
  selectedChannelCustomType,
  isOpenSearchInput,
  memberSearchInputValue,
  onSelectChannelCustomType,
  toggleSearchInputVisible,
  onSearch
}) => {
  const {
    setIsChatPopupOpen
  } = useChatPopupStore();
  return /*#__PURE__*/_jsxs("div", {
    className: "h-fit",
    children: [/*#__PURE__*/_jsxs(S.ChatRoomHeaderLayout, {
      className: "drag-handle",
      children: [/*#__PURE__*/_jsx("div", {
        className: "middle title",
        children: "\uD559\uC0DD \uBAA9\uB85D"
      }), /*#__PURE__*/_jsxs("div", {
        className: "flex items-center chat-header-button",
        children: [/*#__PURE__*/_jsx(Button, {
          variant: "Ghost",
          icon: /*#__PURE__*/_jsx(IconSearch, {}),
          size: "Medium",
          onClick: () => toggleSearchInputVisible(!isOpenSearchInput)
        }), /*#__PURE__*/_jsx(Button, {
          variant: "Ghost",
          icon: /*#__PURE__*/_jsx(IconX, {}),
          size: "Medium",
          onClick: () => setIsChatPopupOpen(false)
        })]
      })]
    }), isOpenSearchInput && /*#__PURE__*/_jsx("div", {
      className: "p-2",
      children: /*#__PURE__*/_jsx(SearchInput, {
        autoFocus: true,
        onEnterKeyDown: value => onSearch(value),
        defaultValue: memberSearchInputValue,
        placeholder: "\uAC80\uC0C9"
      })
    }), /*#__PURE__*/_jsxs(S.GroupChannelListFilteringSection, {
      children: [/*#__PURE__*/_jsx("button", {
        className: `${selectedChannelCustomType === GroupChannelCustomType.IN_CLASS ? 'selected' : ''}`,
        onClick: () => onSelectChannelCustomType(GroupChannelCustomType.IN_CLASS),
        children: "\uCD9C\uC11D"
      }), /*#__PURE__*/_jsx("button", {
        className: `${selectedChannelCustomType === GroupChannelCustomType.AT_HOME ? 'selected' : ''}`,
        onClick: () => onSelectChannelCustomType(GroupChannelCustomType.AT_HOME),
        children: "\uBBF8\uCD9C\uC11D"
      }), /*#__PURE__*/_jsx("button", {
        className: `${!selectedChannelCustomType ? 'selected' : ''}`,
        onClick: () => onSelectChannelCustomType(null),
        children: "\uC804\uCCB4"
      })]
    })]
  });
};
const CustomGroupChannelPreview = ({
  channel,
  studentInfo
}) => {
  const {
    state: {
      onChannelSelect
    }
  } = useGroupChannelList();
  const studyStatus = getStudentStudyStatus(studentInfo?.isAnomalous, studentInfo?.isActive);
  const isAbnormal = studyStatus === StudyStatus.WARNING;
  return /*#__PURE__*/_jsxs("div", {
    onClick: () => {
      onChannelSelect(channel);
    },
    className: "flex w-full gap-2 p-4 border-b border-solid cursor-pointer border-my-gray-90",
    children: [/*#__PURE__*/_jsx("div", {
      className: "",
      children: /*#__PURE__*/_jsxs("div", {
        className: "relative flex w-10 h-10 rounded-[50%]",
        children: [/*#__PURE__*/_jsx(UserAvatarProfile, {
          name: studentInfo ? studentInfo.avatarName : '',
          variant: "beam"
        }), /*#__PURE__*/_jsx("div", {
          className: "absolute bottom-[-2px] right-[-2px]",
          children: /*#__PURE__*/_jsx("div", {
            className: `border-[2px] border-solid rounded-full border-my-gray-100 box-content`,
            children: /*#__PURE__*/_jsx(StudyStatusLight, {
              studyStatus: studyStatus
            })
          })
        })]
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: "flex-grow min-w-[1px] flex flex-col gap-1 justify-center",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "flex flex-wrap items-center gap-1",
        children: [/*#__PURE__*/_jsx("p", {
          className: "text-label-lg-700",
          children: studentInfo?.studentName
        }), studentInfo?.school && /*#__PURE__*/_jsx("p", {
          className: "text-label-sm-600 text-my-gray-50",
          children: studentInfo.school.name
        }), isAbnormal && /*#__PURE__*/_jsx("span", {
          className: `text-my-red-50 text-label-xs-600 bg-my-red-95 py-[0.125rem] px-1 rounded-xl ml-1`,
          children: "\uD559\uC2B5 \uC9C0\uC5F0"
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: "text-body-md-500",
        style: {
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: '1'
        },
        children: channel.lastMessage.isFileMessage() || channel.lastMessage.isMultipleFilesMessage() ? '사진' : channel.lastMessage.customType === ChatMessageType.GOOGLE_MEET ? '화상 회의' : channel.lastMessage.customType === ChatMessageType.QUESTION ? '새로운 질문이 있습니다.' : channel.lastMessage.message
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: "flex flex-col items-end justify-start gap-1 min-w-[4rem]",
      children: [/*#__PURE__*/_jsx("div", {
        className: "text-label-sm-500 text-my-gray-50",
        children: /*#__PURE__*/_jsx(DateTimeRelativeText, {
          dateTime: DateTime.fromMillis(channel.lastMessage.createdAt)
        })
      }), channel.unreadMessageCount > 0 && /*#__PURE__*/_jsx("div", {
        className: `py-[0.19rem] px-[0.38rem] w-fit text-my-gray-100 text-label-xs-700 bg-my-orange-50  ${channel.unreadMessageCount > 100 ? 'rounded-[62.4375rem]' : channel.unreadMessageCount > 9 ? 'rounded-[45%]' : 'rounded-[50%]'}`,
        children: channel.unreadMessageCount > 99 ? `99+` : channel.unreadMessageCount
      })]
    })]
  }, channel.url);
};
const CustomGroupChannelList = ({
  selectedChannelCustomType,
  isOpenSearchInput,
  memberSearchInputValue,
  channelUrlsIncludeSearchMember,
  onSelectChannelCustomType,
  toggleSearchInputVisible,
  onSearch
}) => {
  const globalState = useSendbirdStateContext();
  const sbInstance = globalState?.stores?.sdkStore?.sdk;
  const {
    clickedChatNotification,
    setClickedChatNotification,
    setUnreadCount
  } = useChatStore();
  const {
    channelStudentExtraInfoMap,
    setChannelStudentExtraInfoMap
  } = useGroupChannelListStore();
  const {
    state: {
      groupChannels,
      onChannelSelect
    }
  } = useGroupChannelList();
  const hasNoSearchResults = !_.isNil(channelUrlsIncludeSearchMember) && channelUrlsIncludeSearchMember.length === 0;
  const {
    data: channelStudentsExtraInfoMap
  } = useQuery(['GET_STUDENTS_EXTRA_INFO', groupChannels, hasNoSearchResults], async () => {
    const groupChannelsUrls = [];
    const studentIdChannelMap = new Map();
    const notCachedMetaDataChannels = [];
    _.map(groupChannels, channel => {
      groupChannelsUrls.push(channel.url);
      if (!_.isEmpty(channel.cachedMetaData) && !_.isNil(channel.cachedMetaData['studentId'])) {
        studentIdChannelMap.set(Number(channel.cachedMetaData['studentId']), channel);
      } else {
        notCachedMetaDataChannels.push(channel);
      }
    });
    const hasNewChannelToCheck = _.difference(groupChannelsUrls, Object.keys(channelStudentExtraInfoMap)).length > 0;
    if (hasNewChannelToCheck) {
      const _channelStudentExtraInfoMap = {};
      const studentIds = Array.from(studentIdChannelMap.keys());
      if (notCachedMetaDataChannels.length > 0) {
        await Promise.allSettled(_.map(notCachedMetaDataChannels, channel => {
          return channel.getAllMetaData();
        })).then(results => {
          results.forEach((result, index) => {
            if (result.status === 'fulfilled' && !_.isEmpty(result.value)) {
              const studentId = Number(result.value.studentId);
              if (!_.isNaN(studentId)) {
                studentIds.push(studentId);
                studentIdChannelMap.set(studentId, notCachedMetaDataChannels[index]);
              }
            }
          });
        });
      }
      const studentsExtrasResponse = await getStudentsExtrasApi({
        studentIds
      });
      _.map(studentsExtrasResponse.data, student => {
        _channelStudentExtraInfoMap[studentIdChannelMap.get(student.studentId).url] = {
          ...student
        };
      });
      setChannelStudentExtraInfoMap(_channelStudentExtraInfoMap);
      return _channelStudentExtraInfoMap;
    } else {
      return channelStudentExtraInfoMap;
    }
  }, {
    enabled: groupChannels?.length > 0 && !hasNoSearchResults,
    keepPreviousData: true
  });
  useEffect(() => {
    if (!_.isEmpty(clickedChatNotification) && groupChannels.length > 0) {
      onChannelSelect(groupChannels.find(channel => channel.url === clickedChatNotification.channelUrl));
      setClickedChatNotification(null);
    }
  }, [clickedChatNotification?.channelUrl, groupChannels]);
  useEffect(() => {
    if (sbInstance && sbInstance.currentUser && sbInstance.groupChannel) {
      sbInstance.groupChannel.getTotalUnreadMessageCount().then(count => setUnreadCount(count)).catch(() => setUnreadCount(0));
    }
  }, [sbInstance]);
  const channelListHeader = /*#__PURE__*/_jsx(CustomGroupChannelListHeader, {
    selectedChannelCustomType: selectedChannelCustomType,
    isOpenSearchInput: isOpenSearchInput,
    memberSearchInputValue: memberSearchInputValue,
    onSelectChannelCustomType: onSelectChannelCustomType,
    toggleSearchInputVisible: toggleSearchInputVisible,
    onSearch: onSearch
  });
  if (!_.isEmpty(channelStudentsExtraInfoMap) && !hasNoSearchResults) {
    return /*#__PURE__*/_jsx(GroupChannelListUI, {
      renderHeader: () => channelListHeader,
      renderChannelPreview: props => /*#__PURE__*/_jsx(CustomGroupChannelPreview, {
        channel: props.channel,
        studentInfo: channelStudentsExtraInfoMap[props.channel.url]
      }),
      renderPlaceHolderEmptyList: () => /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(EmptyContent, {
          text: '목록 없음'
        })
      })
    });
  } else {
    return /*#__PURE__*/_jsxs("section", {
      children: [channelListHeader, /*#__PURE__*/_jsx(CustomGroupChannelListSkeleton, {})]
    });
  }
};
export default CustomGroupChannelList;